/**
 * Performance Optimizations Module
 * Handles lazy loading, preloading, scroll optimization, and scroll-to-top
 */

const SCROLL_TO_TOP_ID = 'scroll-to-top';
const SCROLL_TO_TOP_THRESHOLD = 500;

let imageObserver = null;
let ticking = false;

/**
 * Initialize all performance optimizations
 */
export function initializePerformanceOptimizations() {
    initializeLazyLoading();
    preloadCriticalResources();
    initializeScrollOptimization();
}

/**
 * Initialize lazy loading for images with data-src attribute
 */
function initializeLazyLoading() {
    const images = document.querySelectorAll('img[data-src]');

    imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                loadImage(entry.target);
            }
        });
    });

    images.forEach(img => imageObserver.observe(img));
}

/**
 * Load an image by swapping data-src to src
 * @param {HTMLImageElement} img - Image element to load
 */
function loadImage(img) {
    if (img.dataset.src) {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
        if (imageObserver) {
            imageObserver.unobserve(img);
        }
    }
}

/**
 * Preload critical CSS resources
 */
function preloadCriticalResources() {
    const criticalResources = [
        'assets/css/styles.css',
        'assets/css/responsive.css'
    ];

    criticalResources.forEach(resource => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.href = resource;
        link.as = 'style';
        document.head.appendChild(link);
    });
}

/**
 * Initialize optimized scroll event handling with requestAnimationFrame
 */
function initializeScrollOptimization() {
    document.addEventListener('scroll', handleScrollThrottled);
}

/**
 * Throttled scroll handler using requestAnimationFrame
 */
function handleScrollThrottled() {
    if (!ticking) {
        requestAnimationFrame(() => {
            handleScroll();
            ticking = false;
        });
        ticking = true;
    }
}

/**
 * Handle scroll events - parallax and scroll-to-top button
 */
function handleScroll() {
    const scrollY = window.scrollY;

    applyParallaxEffect(scrollY);
    updateScrollToTopButton(scrollY);
}

/**
 * Apply parallax effect to hero section
 * @param {number} scrollY - Current scroll position
 */
function applyParallaxEffect(scrollY) {
    const hero = document.querySelector('.hero');
    if (hero && scrollY < window.innerHeight) {
        hero.style.transform = `translateY(${scrollY * 0.5}px)`;
    }
}

/**
 * Update scroll-to-top button visibility and position
 * @param {number} scrollY - Current scroll position
 */
function updateScrollToTopButton(scrollY) {
    let scrollTopBtn = document.getElementById(SCROLL_TO_TOP_ID);

    if (scrollY > SCROLL_TO_TOP_THRESHOLD && !scrollTopBtn) {
        createScrollToTopButton();
    } else if (scrollY <= SCROLL_TO_TOP_THRESHOLD && scrollTopBtn) {
        hideScrollToTopButton(scrollTopBtn);
    }
}

/**
 * Create scroll-to-top button
 */
function createScrollToTopButton() {
    const scrollTopBtn = document.createElement('button');
    scrollTopBtn.id = SCROLL_TO_TOP_ID;
    scrollTopBtn.innerHTML = '↑';
    scrollTopBtn.setAttribute('aria-label', 'Scroll to top');

    scrollTopBtn.style.cssText = `
        position: fixed;
        bottom: 90px;
        right: 30px;
        width: 50px;
        height: 50px;
        border-radius: 50%;
        border: none;
        background: var(--primary-crimson);
        color: white;
        font-size: 1.5rem;
        cursor: pointer;
        box-shadow: 0 4px 15px rgba(220, 20, 60, 0.4);
        transition: all 0.3s ease;
        z-index: 998;
        opacity: 0;
        transform: translateY(20px);
    `;

    scrollTopBtn.addEventListener('click', scrollToTop);
    document.body.appendChild(scrollTopBtn);

    // Animate in
    setTimeout(() => {
        scrollTopBtn.style.opacity = '1';
        scrollTopBtn.style.transform = 'translateY(0)';
    }, 100);
}

/**
 * Scroll smoothly to top of page
 */
function scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
}

/**
 * Hide and remove scroll-to-top button
 * @param {HTMLElement} button - Button element to hide
 */
function hideScrollToTopButton(button) {
    button.style.opacity = '0';
    button.style.transform = 'translateY(20px)';
    setTimeout(() => button?.remove(), 300);
}

/**
 * Cleanup performance optimizations
 */
export function cleanupPerformanceOptimizations() {
    if (imageObserver) {
        imageObserver.disconnect();
        imageObserver = null;
    }

    document.removeEventListener('scroll', handleScrollThrottled);

    const scrollTopBtn = document.getElementById(SCROLL_TO_TOP_ID);
    if (scrollTopBtn) {
        scrollTopBtn.remove();
    }
}
